/**
 * useDatasetView.ts - useDatasetView Hook
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { useMemo, useCallback } from 'react';

/**
 * Hook for dataset-aware views
 * 
 * @param {string} viewType - Type of view (e.g., 'outliner', 'kanban', 'mindmap')
 * @param {Object} datasetContext - Dataset context from useDataset()
 * @param {Object} dataContext - Data context from useData()
 * @returns {Object} View utilities and data access methods
 */
export const useDatasetView = (viewType, datasetContext, dataContext) => {
  const { getSmartFieldMappings } = datasetContext;
  const { getAllItems, updateItem, addItem, deleteItem } = dataContext;
  
  // Get field mappings for this view
  const fieldMappings = useMemo(() => 
    getSmartFieldMappings(viewType), 
    [getSmartFieldMappings, viewType]
  );
  
  // Get items directly from dataContext.items and convert to array
  // This prevents infinite loops by not depending on the getAllItems function
  const items = useMemo(() => {
    const itemsObj = dataContext.items;
    const itemsArray = itemsObj ? Object.values(itemsObj) : [];
    return itemsArray;
  }, [dataContext.items]);
  
  /**
   * Get a field value from an item using view field name
   * @param {Object} item - The item object
   * @param {string} viewField - View-specific field name (e.g., 'itemLabel')
   * @returns {*} The field value
   */
  const getField = useCallback((item, viewField) => {
    const datasetField = fieldMappings[viewField];
    return item?.[datasetField];
  }, [fieldMappings]);
  
  /**
   * Set a field value on an item using view field name
   * @param {string} itemId - Item ID
   * @param {string} viewField - View-specific field name
   * @param {*} value - Value to set
   */
  const setField = useCallback((itemId, viewField, value) => {
    const datasetField = fieldMappings[viewField];
    if (datasetField) {
      updateItem(itemId, { [datasetField]: value });
    }
  }, [fieldMappings, updateItem]);
  
  /**
   * Create a new item with view-specific fields
   * @param {Object} viewFields - Object with view field names as keys
   * @returns {string} New item ID
   */
  const createItem = useCallback((viewFields) => {
    const datasetFields = {};
    Object.entries(viewFields).forEach(([viewField, value]) => {
      const datasetField = fieldMappings[viewField];
      if (datasetField) {
        datasetFields[datasetField] = value;
      }
    });
    return addItem(datasetFields);
  }, [fieldMappings, addItem]);
  
  /**
   * Transform an item to view-specific field names
   * Useful for rendering in the view
   * @param {Object} item - Dataset item
   * @returns {Object} Item with view field names
   */
  const toViewItem = useCallback((item) => {
    if (!item) return null;
    
    const viewItem = { ...item }; // Keep all original fields
    
    // Add view-specific field aliases
    Object.entries(fieldMappings).forEach(([viewField, datasetField]) => {
      viewItem[viewField] = item[datasetField];
    });
    
    return viewItem;
  }, [fieldMappings]);
  
  /**
   * Transform multiple items to view-specific field names
   * @param {Array} items - Array of dataset items
   * @returns {Array} Items with view field names
   */
  const toViewItems = useCallback((items) => {
    return items.map(toViewItem);
  }, [toViewItem]);
  
  return {
    // Field mappings
    fieldMappings,
    
    // Field access
    getField,
    setField,
    
    // Item creation
    createItem,
    
    // Item transformation
    toViewItem,
    toViewItems,
    
    // Direct data access
    items,
    updateItem,
    deleteItem,
    
    // Pass through other data context methods
    ...dataContext
  };
};

export default useDatasetView;
